import type { Entity, EntityObjectCompressed } from '@einhaus/project314-types';
import { useEffect, useState, useMemo } from 'react';
import { trpcUnbatchedClient } from '@/api/trpc.ts';
import { showToast } from '@/components/Layout/Toast.tsx';

// Helper hook to batch fetching entities requests
export const useFetchEntities = (entityObjects: EntityObjectCompressed[]) => {
    const [entities, setEntities] = useState<Entity[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Create a stable key for dependency comparison
    const entityObjectsKey = useMemo(() => {
        return entityObjects
            .map((e) => `${e.i}_${e.t}`)
            .sort()
            .join(',');
    }, [entityObjects]);

    useEffect(() => {
        const fetchBatch = async (batch: EntityObjectCompressed[]) => {
            return trpcUnbatchedClient.entity.fetchEntities.query({ entities: batch });
        };

        const fetchEntitiesInBatches = async () => {
            const batchSize = 150;
            const batchPromises = [];

            setIsLoading(true);
            setEntities([]); // Reset entities when fetching new batch

            // Create all batch promises
            for (let i = 0; i < entityObjects.length; i += batchSize) {
                const batch = entityObjects.slice(i, i + batchSize);
                batchPromises.push(fetchBatch(batch));
            }

            try {
                // Execute all batch requests concurrently
                const results = await Promise.all(batchPromises);

                // Process all results
                const allEntities: Entity[] = [];

                results.forEach((response) => {
                    if (!response.success) {
                        showToast(response.errorMessages[0], 'error');
                    } else {
                        allEntities.push(...response.entities);
                    }
                });

                setEntities(allEntities);
            } catch (error) {
                showToast('Failed to fetch entities', 'error');
                console.error('Error fetching entities:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (entityObjects.length) void fetchEntitiesInBatches();
    }, [entityObjectsKey]);

    return { isLoading, entities };
};
